import { FC } from 'react';
import { Grid, OutlinedInput } from '@mui/material';
import { IntlShape, useIntl } from 'react-intl';
import { z } from 'zod';
import { graphql } from '../../gql';
import { useApiMutation, useApiQuery } from '../../query/hooks';
import { FormField } from '../../form/FormField';
import { ControlledFormField } from '../../form/ControlledFormField';
import { FormErrors } from '../../form/FormErrors';
import { PhaseSelect } from '../PhaseSelect';
import { useTypedForm } from '../../../../shared/fe/hooks/useForm';
import { mutationResponseHandler } from '../../utils/mutationResponseHandler';
import { ModalContent } from '../../modal/components/ModalContent';
import { ModalActions } from '../../modal/components/ModalActions';
import { useModalContext } from '../../modal/ModalContext';
import { useSnackbar } from '../../store/snackbar';

const journeyDetail_activityCount = graphql(`
	query journeyDetail_activityCount($phaseId: UUID!) {
		listTouchpoint(filter: { phase: { id: { eq: $phaseId } } }) {
			id
			order
		}
	}
`);

const journeyDetail_createActivity = graphql(`
	mutation journeyDetail_createActivity($phaseId: UUID!, $name: String!, $order: Int!) {
		createActivity(
			data: {
				name: $name
				description: ""
				touchpoint: { create: { order: $order, type: activity, phase: { connect: { id: $phaseId } } } }
			}
		) {
			ok
			node {
				id
				name
				touchpoint {
					id
				}
			}
			errorMessage
		}
	}
`);

const journeyDetail_updatePhaseTouchpointsOrder = graphql(`
	mutation journeyDetail_updatePhaseTouchpointsOrder($phaseId: UUID!, $touchpoints: [PhaseUpdateTouchpointsEntityRelationInput!]!) {
		updatePhase(by: { id: $phaseId }, data: { touchpoints: $touchpoints }) {
			ok
			errorMessage
		}
	}
`);

const createFormSchema = (intl: IntlShape) =>
	z.object({
		name: z.string().nonempty(intl.formatMessage({ id: 'form.create-activity.error.empty-name' })),
		phaseId: z.string().nonempty(intl.formatMessage({ id: 'form.create-activity.error.empty-phase' })),
	});

type FormValues = z.infer<ReturnType<typeof createFormSchema>>;

const CreateActivityModalSchema = z.object({
	journeyId: z.string(),
	phaseId: z.string().optional(),
	activityOffset: z.string().optional(),
});

type Props = z.infer<typeof CreateActivityModalSchema>;

const CreateActivityModal: FC<Props> = ({ journeyId, phaseId, activityOffset }) => {
	const intl = useIntl();
	const { closeModal } = useModalContext();
	const { mutate: createActivity, isPending } = useApiMutation(journeyDetail_createActivity);
	const { openSnackbar } = useSnackbar();
	const { mutateAsync: updateTouchpointsOrder } = useApiMutation(journeyDetail_updatePhaseTouchpointsOrder, {
		onSuccess: (result) => {
			mutationResponseHandler({
				result: result.updatePhase.ok,
				toastMessages: {
					success: intl.formatMessage({ id: 'detail.activity.created.success' }),
					error: intl.formatMessage({ id: 'timeline.phase.update.error' }),
				},
				errorMessage: result.updatePhase.errorMessage,
				openSnackbar,
			});
		},
	});

	const form = useTypedForm({
		schema: createFormSchema(intl),
		defaultValues: {
			phaseId: phaseId ?? '',
		},
	});

	const { data: activityCount } = useApiQuery(journeyDetail_activityCount, {
		variables: { phaseId: form.watch('phaseId') },
	});

	const activityHighestOrderArray =
		activityCount?.listTouchpoint.reduce((highest, { order }) => {
			if (highest < order) {
				return order;
			}
			return highest;
		}, 0) ?? 0;

	const onSubmit = ({ name, phaseId }: FormValues) => {
		createActivity(
			{
				name,
				phaseId,
				order: activityHighestOrderArray + 1,
			},
			{
				onSuccess: (result) => {
					mutationResponseHandler({
						result: result.createActivity.ok,
						toastMessages: {
							error: intl.formatMessage({ id: 'detail.activity.error.create' }),
						},
						errorMessage: result.createActivity.errorMessage,
						callback: () => {
							if (result.createActivity.node?.touchpoint?.id && activityOffset) {
								const newActivityOrder = Number(activityOffset);
								const touchpointList =
									activityCount?.listTouchpoint.map((item, index) => {
										if (item.order < newActivityOrder) {
											return { id: item.id, order: index };
										}
										return { id: item.id, order: index + 1 };
									}) ?? [];

								touchpointList.push({ id: result.createActivity.node.touchpoint.id, order: newActivityOrder });

								updateTouchpointsOrder({
									phaseId: form.watch('phaseId'),
									touchpoints: touchpointList.map((tp) => ({
										update: { by: { id: tp.id }, data: { order: tp.order } },
									})),
								});
							}
							closeModal();
						},
						openSnackbar,
					});
				},
			},
		);
	};

	return (
		<>
			<ModalContent title={intl.formatMessage({ id: 'form.create-activity.title' })} description={''}>
				<form noValidate>
					<Grid container spacing={3}>
						<FormField
							form={form}
							name={'name'}
							label={intl.formatMessage({ id: 'form.create-activity.label.name' })}
							render={(props) => (
								<OutlinedInput placeholder={intl.formatMessage({ id: 'form.create-activity.placeholder.name' })} {...props} />
							)}
						/>
						<ControlledFormField
							form={form}
							name={'phaseId'}
							label={intl.formatMessage({ id: 'form.create-activity.label.phase' })}
							render={(props) => <PhaseSelect journeyId={journeyId} {...props} />}
						/>
						<FormErrors form={form} />
					</Grid>
				</form>
			</ModalContent>
			<ModalActions
				handleCancelButtonClick={closeModal}
				handlePrimaryButtonClick={form.handleSubmit(onSubmit)}
				cancelButtonText={intl.formatMessage({ id: 'common.cancel' })}
				primaryButtonText={intl.formatMessage({ id: 'common.create' })}
				disablePrimaryButton={isPending}
			/>
		</>
	);
};

export { CreateActivityModal, CreateActivityModalSchema };
